/**
 * Data layer. Loads build:data output from /data/* with optional live overlay.
 */

import type {
  DataSnapshot,
  Skill,
  Category,
  ScoreBucket,
  SkillStatus,
  AggregateStats,
  GeneratedMeta,
  PortfolioSnapshot,
} from "./types";
import type { SkillGraphData } from "./graph-types";
import type { GapsData, OptimizationsData } from "./insights-types";
import type { RunsBundle } from "./runs-types";
import { bucketFor, categoryFor } from "./scoring";
import { applyEnrichments } from "./plugins";
import { fetchLiveMeta, fetchLiveSnapshot, fetchLiveScores } from "./live-api";

async function fetchJSON<T>(path: string): Promise<T> {
  const res = await fetch(path, { cache: "no-cache" });
  if (!res.ok) throw new Error(`Failed to load ${path}: ${res.status}`);
  return res.json() as Promise<T>;
}

const DEFAULT_META: GeneratedMeta = {
  generated: "",
  skillCount: 0,
  avgCombined: 0,
  avgStructural: 0,
  withLlm: 0,
  llmAvailable: false,
  tierCounts: { excellent: 0, good: 0, "needs-work": 0, poor: 0 },
  statusCounts: { healthy: 0, warning: 0, critical: 0 },
  githubRepo: "OnlineChefGroep/skill-grinder",
  gitSha: null,
};

/** Load portfolio snapshot; refetch via React Query invalidate (no module singleton). */
export async function loadDataSnapshot(): Promise<DataSnapshot> {
  const [scores, history, metaRaw, snapshotsRaw] = await Promise.all([
    fetchJSON<{ generated: string; skills: Skill[] }>("/data/scores.json"),
    fetchJSON<{ generated: string; history: DataSnapshot["history"]; kind?: string }>("/data/history.json"),
    fetchJSON<GeneratedMeta>("/data/generated-at.json").catch(() => DEFAULT_META),
    fetchJSON<{ snapshots: PortfolioSnapshot[] }>("/data/snapshots.json").catch(() => ({ snapshots: [] })),
  ]);

  let generated = scores.generated;
  let rawSkills = scores.skills;

  const staticMeta = { ...DEFAULT_META, ...metaRaw, generated: metaRaw.generated || scores.generated };
  const [meta, liveSnapshot, liveScores] = await Promise.all([
    fetchLiveMeta(staticMeta),
    fetchLiveSnapshot(),
    fetchLiveScores(scores.generated),
  ]);

  const live = liveScores as (typeof liveScores & { isNewerThanStatic?: boolean }) | null;
  if (live?.isNewerThanStatic && live.skills?.length) {
    rawSkills = live.skills;
    generated = live.generated ?? generated;
    meta.isNewerThanStatic = true;
    if (live.dataSource === "github" || live.dataSource === "assets" || live.dataSource === "static") {
      meta.dataSource = live.dataSource;
    }
  }

  const enrichedSkills = rawSkills.map(applyEnrichments);

  return {
    generated,
    skills: enrichedSkills,
    history: history.history,
    historyKind: "autoresearch",
    snapshots: snapshotsRaw.snapshots ?? [],
    liveSnapshot,
    meta,
    stats: computeStats(enrichedSkills, meta),
  };
}

/** @deprecated Use loadDataSnapshot via React Query */
export const getSnapshot = loadDataSnapshot;

function computeStats(skills: Skill[], meta?: GeneratedMeta): AggregateStats {
  const total = skills.length;
  const sum = skills.reduce((s, x) => s + x.combinedPercentage, 0);
  const average = meta?.avgCombined ?? (total ? Math.round(sum / total) : 0);
  const averageStructural = meta?.avgStructural ?? (total
    ? Math.round(skills.reduce((s, x) => s + x.structuralPercentage, 0) / total)
    : 0);
  const withLlm = meta?.withLlm ?? skills.filter((s) => s.llmScores !== null).length;
  const above85 = skills.filter((s) => s.combinedPercentage >= 85).length;
  const below70 = skills.filter((s) => s.combinedPercentage < 70).length;
  const broken = skills.reduce((s, x) => s + x.brokenRefs.length, 0);
  const withRouter = skills.filter((s) => s.hasRouter).length;
  const withRefs = skills.filter((s) => s.hasRefs).length;
  const deltas = skills
    .map((s) => s.delta)
    .filter((d): d is number => typeof d === "number");
  const trendDelta = deltas.length
    ? Math.round((deltas.reduce((a, b) => a + b, 0) / deltas.length) * 10) / 10
    : 0;
  const lastRunAt = skills
    .map((s) => s.lastScored)
    .filter(Boolean)
    .map((d) => Date.parse(d!))
    .reduce((max, ts) => (ts > max ? ts : max), 0) || null;

  const categoryCounts: Record<Category, number> = {
    agent: 0,
    skill: 0,
    utrecht: 0,
    workflow: 0,
    other: 0,
  };
  const bucketCounts: Record<ScoreBucket, number> = meta?.tierCounts ?? {
    excellent: 0,
    good: 0,
    "needs-work": 0,
    poor: 0,
  };
  const statusCounts: Record<SkillStatus, number> = meta?.statusCounts ?? {
    healthy: 0,
    warning: 0,
    critical: 0,
  };

  for (const s of skills) {
    if (s.category in categoryCounts) categoryCounts[s.category]++;
    if (!meta?.tierCounts && s.bucket in bucketCounts) bucketCounts[s.bucket]++;
    if (!meta?.statusCounts && s.status in statusCounts) statusCounts[s.status]++;
  }

  return {
    total,
    average,
    averageStructural,
    withLlm,
    above85,
    below70,
    broken,
    withRouter,
    withRefs,
    trendDelta,
    lastRunAt,
    categoryCounts,
    bucketCounts,
    statusCounts,
  };
}

export function findSkill(skills: Skill[], name: string): Skill | undefined {
  return skills.find((s) => s.name === name);
}

export async function getSkillBody(name: string): Promise<string> {
  const data = await fetchJSON<{ body: string }>(`/data/skill-bodies/${name}.json`);
  return data.body;
}

export async function loadSkillGraph(): Promise<SkillGraphData> {
  return fetchJSON<SkillGraphData>("/data/skill-graph.json");
}

export async function loadGaps(): Promise<GapsData> {
  return fetchJSON<GapsData>("/data/gaps.json");
}

export async function loadRuns(): Promise<RunsBundle> {
  return fetchJSON<RunsBundle>("/data/runs.json");
}

export async function loadOptimizations(): Promise<OptimizationsData> {
  try {
    const raw = await fetchJSON<OptimizationsData & Record<string, unknown>>("/data/optimizations.json");
    if (raw.bySkill) return { generated: raw.generated ?? "", bySkill: raw.bySkill };
    const bySkill: OptimizationsData["bySkill"] = {};
    for (const [key, val] of Object.entries(raw)) {
      if (key === "generated" || key === "bySkill") continue;
      if (val && typeof val === "object" && "suggestions" in val) {
        bySkill[key] = val as OptimizationsData["bySkill"][string];
      }
    }
    return { generated: String(raw.generated ?? ""), bySkill };
  } catch {
    return { generated: "", bySkill: {} };
  }
}

export type { GeneratedMeta, PortfolioSnapshot };

export { bucketFor, categoryFor };